/*
 * Yoni loop — makes a snap-scrolling card track endless: after the last card the first one
 * comes back, both ways, so the row never ends on a cut card. Shared by yoni-carousel.js and
 * yoni-benefits-cards.js (each section loads this file before its own script).
 *
 * How: the cards are copied before and after the real ones, and the track rests on the real
 * set. Whenever a scroll comes to rest on a copy it jumps by exactly one set, which looks
 * identical. Copies are hidden from screen readers and the Tab order but stay clickable (at
 * some stops most of the visible row is copies). One copy each side when the real cards
 * overflow the track; when they all fit, as many as it takes to fill it, so a rail that runs
 * to the screen edge never stops short of it (owner 2026-10-22: repeats are fine).
 *
 *   var loop = yoniLoop(track);  loop.active, loop.size, loop.start (first real card's index), loop.refresh()
 *
 * Also yoniGlide(track, left): the arrows' scroll. Native smooth scrolling has no easing
 * control and, on a snapping row, could end a few px off a stop and snap again (a second
 * small jolt). This eases scrollLeft straight to the stop on the theme's drawer curve with
 * snapping paused, then hands back to the browser. Instant when motion is off or reduced.
 *   yoniGlide(track, left);  yoniGlide.target(track) -> where a running glide ends, or null
 */
(function () {
  if (window.yoniLoop) return;
  var CLONE = 'data-carousel-clone';

  function motionOk() {
    return !document.body.classList.contains('yoni-motion-off') &&
      !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  // cubic-bezier(0.32, 0.72, 0, 1) = --ease-drawer in yoni-tokens.css: a soft start, a long settle.
  function drawer(p) {
    var x1 = 0.32, y1 = 0.72, x2 = 0, y2 = 1, lo = 0, hi = 1, t = p;
    function bx(t) { return 3 * x1 * t * (1 - t) * (1 - t) + 3 * x2 * t * t * (1 - t) + t * t * t; }
    for (var i = 0; i < 24; i++) { if (bx(t) < p) lo = t; else hi = t; t = (lo + hi) / 2; }
    return 3 * y1 * t * (1 - t) * (1 - t) + 3 * y2 * t * t * (1 - t) + t * t * t;
  }
  function stopGlide(track, done) {
    var g = track._yoniGlide;
    if (!g) return;
    cancelAnimationFrame(g.raf);
    track._yoniGlide = null;
    track.style.scrollSnapType = '';
    if (done) track.dispatchEvent(new Event('scrollend')); // the loop settles once, at the end
  }
  window.yoniGlide = function (track, left) {
    stopGlide(track, false);
    left = Math.max(0, Math.min(track.scrollWidth - track.clientWidth, left));
    if (!motionOk()) { track.scrollTo({ left: left, behavior: 'instant' }); return; }
    if (!track._yoniGlideWired) {
      // A visitor taking over (wheel, touch, press) ends the glide where it is.
      track._yoniGlideWired = true;
      ['wheel', 'touchstart', 'pointerdown'].forEach(function (ev) {
        track.addEventListener(ev, function () { stopGlide(track, false); }, { passive: true });
      });
    }
    var from = track.scrollLeft, dist = left - from, t0 = null;
    var dur = Math.min(720, 380 + Math.abs(dist) * 0.22); // one card ≈ 480ms, a long jump never drags
    var g = track._yoniGlide = { to: left, raf: 0 };
    track.style.scrollSnapType = 'none';
    g.raf = requestAnimationFrame(function frame(now) {
      if (t0 === null) t0 = now;
      var p = Math.min(1, (now - t0) / dur);
      track.scrollLeft = from + dist * drawer(p);
      if (p < 1) g.raf = requestAnimationFrame(frame);
      else stopGlide(track, true);
    });
  };
  window.yoniGlide.target = function (track) { return track._yoniGlide ? track._yoniGlide.to : null; };

  function copy(el) {
    var c = el.cloneNode(true);
    c.setAttribute(CLONE, '');
    c.setAttribute('aria-hidden', 'true');
    [c].concat(Array.prototype.slice.call(c.querySelectorAll('*'))).forEach(function (n) {
      n.removeAttribute('id');
      n.removeAttribute('aria-controls');
      // Theme Editor block markers stay on the real card only.
      Array.prototype.slice.call(n.attributes).forEach(function (a) {
        if (a.name.indexOf('data-shopify') === 0) n.removeAttribute(a.name);
      });
      if (n.matches('a[href], button, input, select, textarea, [tabindex]')) n.setAttribute('tabindex', '-1');
    });
    return c;
  }

  window.yoniLoop = function (track) {
    var api = { active: false, size: 0, start: 0, refresh: refresh };
    var touching = false, timer = null, lastW = -1, settleTimer = null, reps = 1;

    function originals() {
      return Array.prototype.slice.call(track.querySelectorAll(':scope > :not([' + CLONE + '])'));
    }
    // One set's width: from the copy just before the first real card to that card.
    function setWidth() {
      var o = originals()[0], c = track.children[api.start - api.size];
      return o && c ? o.getBoundingClientRect().left - c.getBoundingClientRect().left : 0;
    }
    function scrollTo(x) { track.scrollTo({ left: x, behavior: 'instant' }); }

    function jump(dx) {
      // Transitions off around the jump: the card now under the pointer would otherwise
      // replay its hover lift / photo zoom.
      track.classList.add('is-looping');
      scrollTo(track.scrollLeft + dx);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () { track.classList.remove('is-looping'); }, 250);
    }
    // The card at the rest position (index among all children). Measured against the cards, not
    // against the set width: scroll-padding can rest each stop a few px off a card's own offset.
    function nearest() {
      var kids = track.children, first = track.firstElementChild, best = 0, bestD = Infinity;
      if (!first) return 0;
      for (var i = 0; i < kids.length; i++) {
        var d = Math.abs(kids[i].getBoundingClientRect().left - first.getBoundingClientRect().left - track.scrollLeft);
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    }
    function settle() {
      if (!api.active || touching || track._yoniGlide) return; // a glide settles when it ends
      var i = nearest(), n = api.size, lo = api.start;
      if (i < lo) jump(Math.ceil((lo - i) / n) * setWidth());
      else if (i >= lo + n) jump(-Math.floor((i - lo) / n) * setWidth());
    }
    // The real card at the rest position, so a rebuild (resize) keeps the visitor's place.
    function current() { return api.size ? nearest() % api.size : 0; }

    function refresh() {
      if (track.clientWidth === lastW) return api;
      lastW = track.clientWidth;
      stopGlide(track, false);
      var keep = api.active ? current() : 0;
      track.querySelectorAll(':scope > [' + CLONE + ']').forEach(function (c) { c.remove(); });
      api.active = false;
      var o = originals();
      api.size = o.length;
      if (o.length < 2 || !track.clientWidth) return api;
      // One set = the real cards plus a gap: where the first card would come round again.
      var a = o[0].getBoundingClientRect(), set = o[o.length - 1].getBoundingClientRect().right - a.left + o[1].getBoundingClientRect().left - a.right;
      reps = track.scrollWidth - track.clientWidth < 40 ? Math.max(1, Math.ceil(track.clientWidth / set)) : 1;
      var before = document.createDocumentFragment(), after = document.createDocumentFragment();
      for (var r = 0; r < reps; r++) o.forEach(function (el) { before.appendChild(copy(el)); after.appendChild(copy(el)); });
      track.insertBefore(before, o[0]);
      track.appendChild(after);
      api.active = true;
      api.start = reps * o.length;
      scrollTo(o[keep].getBoundingClientRect().left - track.firstElementChild.getBoundingClientRect().left);
      return api;
    }

    // Rest = scrollend; without it (older Safari), no scroll event for 150ms and no finger down.
    var hasEnd = 'onscrollend' in window;
    function soon() { clearTimeout(timer); timer = setTimeout(settle, 150); }
    if (hasEnd) track.addEventListener('scrollend', settle);
    else track.addEventListener('scroll', soon, { passive: true });
    track.addEventListener('touchstart', function () { touching = true; }, { passive: true });
    ['touchend', 'touchcancel'].forEach(function (ev) {
      track.addEventListener(ev, function () { touching = false; if (!hasEnd) soon(); }, { passive: true });
    });

    return refresh();
  };
})();
